import {IServerResponse} from "../../../common-models"
import {Module} from "vuex";
import type { IRootState } from "../../../../store/root-state";
import {ActionContext} from "vuex";
import * as R from "ramda";
import {FieldHelpData} from "./field-help-data"
import {messageDispatchHelper} from "../../../../user-message/user-message-store"
import {USER_MESSAGE_LEVEL} from "../../../../user-message/user-message-models"
import {FieldHelpService} from "./field-help-service"

// Import service locator functions
import {
  getFieldHelpData,
  getFieldHelpService,
} from "../../../../common/services/service-registry";

//  Text | HTML | Url
export type FIELD_HELP_MESSAGE_TYPE = "T" | "H" | "U";

export interface IHelpDataSimple {
    title: string;
    data: string;
    type: FIELD_HELP_MESSAGE_TYPE;
}

export interface IHelpData extends IHelpDataSimple{
    id: number;
    key: string;
    preload: boolean;
}

export interface IFieldHelpStoreState {
    helpKey: string;
    showHelp: boolean;
    helpData: IHelpData;
    isLoading: boolean;
    serverCache: Record<string, IHelpData>;
}

export enum FIELD_HELP_STORE_CONST {

    CONST_MODULE_NAME =  "FIELD_HELP_STORE_CONST",

    // <MUTATIONS>
    MUTATIONS_SET_SHOW_HELP =  "MUTATIONS_SET_SHOW_HELP",
    MUTATIONS_SET_HELP_DATA =  "MUTATIONS_SET_HELP_DATA",
    MUTATIONS_SET_HELP_DATA_SERVER_CACHE =  "MUTATIONS_SET_HELP_DATA_SERVER_CACHE",
    MUTATIONS_SET_HELP_DATA_LOADING =  "MUTATIONS_SET_HELP_DATA_LOADING",
    // </MUTATIONS>

    // <ACTIONS>
    ACTIONS_GET_HELP_FORM_SERVER =  "ACTIONS_GET_HELP_FORM_SERVER",
    ACTIONS_SUBMIT_HELP =  "ACTIONS_SUBMIT_HELP"
    // </ACTIONS>
}

const mutations = {
    [FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING](state: IFieldHelpStoreState, isLoading: boolean) {
        state.isLoading = isLoading;
    },
    [FIELD_HELP_STORE_CONST.MUTATIONS_SET_SHOW_HELP](state: IFieldHelpStoreState, showHelp: boolean) {
        if (!showHelp) {
            //  Clear out whatever is there.
            state.helpData = getFieldHelpService().factoryHelpData();
        }
        state.showHelp = showHelp;
    },
    [FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA](state: IFieldHelpStoreState, helpData: IHelpData) {
        if (helpData) {
            state.helpData = R.clone(helpData);
            state.showHelp = true;
        }
    },
    [FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_SERVER_CACHE](state: IFieldHelpStoreState, helpData: IHelpData) {
        if (helpData) {
            state.serverCache[helpData.key] = R.clone(helpData);
        }
    }
};

// For namespaced access, we need to provide the full namespaced mutation names
export const fieldHelpMutations = {
    [FIELD_HELP_STORE_CONST.CONST_MODULE_NAME + '/' + FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING]: mutations[FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING],
    [FIELD_HELP_STORE_CONST.CONST_MODULE_NAME + '/' + FIELD_HELP_STORE_CONST.MUTATIONS_SET_SHOW_HELP]: mutations[FIELD_HELP_STORE_CONST.MUTATIONS_SET_SHOW_HELP],
    [FIELD_HELP_STORE_CONST.CONST_MODULE_NAME + '/' + FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA]: mutations[FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA],
    [FIELD_HELP_STORE_CONST.CONST_MODULE_NAME + '/' + FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_SERVER_CACHE]: mutations[FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_SERVER_CACHE],
};

const getters = {};

const actions = {
    [FIELD_HELP_STORE_CONST.ACTIONS_GET_HELP_FORM_SERVER](
        context: ActionContext<IFieldHelpStoreState, IRootState>,
        payload: {
            key: string
        }
    ) {
        context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING, true);
        context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_SHOW_HELP, true);

        return getFieldHelpData().readHelp(payload.key)
            .then( (response: IServerResponse<IHelpData>) => {
                if (response.errNo > 0) {
                    messageDispatchHelper(response.error, USER_MESSAGE_LEVEL.ERROR.toString());
                    return getFieldHelpService().factoryHelpData();
                }
                context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA, response.data);
                context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_SERVER_CACHE, response.data);
                return response.data;
            })
            .catch((error) => {
                messageDispatchHelper(error, USER_MESSAGE_LEVEL.ERROR.toString());
                return getFieldHelpService().factoryHelpData();
            })
            .finally(() => {
                context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING, false);
            })
    },

    [FIELD_HELP_STORE_CONST.ACTIONS_SUBMIT_HELP](
        context: ActionContext<IFieldHelpStoreState, IRootState>,
        payload: {
            helpData: IHelpData
        }
    ): Promise<IHelpData> {
        context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING, true);

        const helpData = payload.helpData;
        let prom: Promise<IServerResponse<IHelpData>>;
        if (helpData.id === 0) {
            prom = getFieldHelpData().create(helpData);
        } else {
            prom = getFieldHelpData().update(helpData);
        }

        return prom
            .then( (response: IServerResponse<IHelpData>) => {
                if (response.errNo > 0) {
                    messageDispatchHelper(response.error, USER_MESSAGE_LEVEL.ERROR.toString());
                    return getFieldHelpService().factoryHelpData();
                }
                context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA, response.data);
                return response.data;
            })
            .catch((error) => {
                messageDispatchHelper(error, USER_MESSAGE_LEVEL.ERROR.toString());
                return getFieldHelpService().factoryHelpData();
            })
            .finally(() => {
                context.commit(FIELD_HELP_STORE_CONST.MUTATIONS_SET_HELP_DATA_LOADING, false);
            })
    }
};

/**
 * Default state factory for field help store
 * This replaces the service call that was causing the circular dependency
 */
function getDefaultFieldHelpState(): IFieldHelpStoreState {
  return {
    helpKey: "",
    showHelp: false,
    isLoading: false,
    helpData: {
      id: 0,
      key: "",
      title: "",
      type: "T",
      data: "",
      preload: false
    },
    serverCache: {}
  };
}

/**
 * Create field help store module with dependency injection
 */
export function createFieldHelpStore(serviceContainer: any): Module<IFieldHelpStoreState, IRootState> {
  return {
    namespaced: true,
    state: getDefaultFieldHelpState(),
    mutations,
    getters,
    actions,
  };
}

// Legacy export for backward compatibility (will be removed after full migration)
export const fieldHelpStore: Module<IFieldHelpStoreState, IRootState> = {
  namespaced: true,
  state: getDefaultFieldHelpState(),
  mutations,
  getters,
  actions
};
